import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  MoreHorizontal,
  Eye,
  CheckCircle2,
  CalendarDays,
  StickyNote,
  UserPlus,
  Undo2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Task, TaskStatus } from "@/app/types/task";
import { taskService } from "@/app/services/taskService";
import { createNote } from "@/app/services/noteService";
import { ConfirmationDialog } from "@/app/components/ConfirmationDialog";

const dispatchTaskEvent = (name: string, task: Task) =>
  document.dispatchEvent(new CustomEvent(name, { detail: task }));

const iconBtn = "h-7 w-7 p-0 text-muted-foreground hover:text-foreground";

/** Inline due-date editor — popover with an inline calendar + quick presets. */
const DueDatePopoverCell: React.FC<{ task: Task }> = ({ task }) => {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [pendingDate, setPendingDate] = useState<Date | null>(null);
  const selected = task.due_date ? new Date(task.due_date) : undefined;

  const mutation = useMutation({
    mutationFn: (due_date: string) => taskService.update(task.id, { due_date }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      toast.success("Due date updated");
      setPendingDate(null);
    },
    onError: (error: any) => {
      toast.error("Could not update due date", {
        description: error?.response?.data?.detail || error?.message,
      });
    },
  });

  // Selecting a date stages it for confirmation (no API call yet).
  const pick = (date: Date | undefined) => {
    if (date) {
      setOpen(false);
      setPendingDate(date);
    }
  };

  const setPreset = (daysFromToday: number) => {
    const d = new Date();
    d.setDate(d.getDate() + daysFromToday);
    pick(d);
  };

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <Popover open={open} onOpenChange={setOpen}>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <Button variant="ghost" className={iconBtn} title="Change due date">
                  <CalendarDays className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent>Change due date</TooltipContent>
            <PopoverContent
              align="end"
              className="w-auto p-0"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-1.5 border-b p-2">
                <Button variant="outline" size="sm" className="h-7 text-xs flex-1" onClick={() => setPreset(0)}>
                  Today
                </Button>
                <Button variant="outline" size="sm" className="h-7 text-xs flex-1" onClick={() => setPreset(1)}>
                  Tomorrow
                </Button>
                <Button variant="outline" size="sm" className="h-7 text-xs flex-1" onClick={() => setPreset(7)}>
                  +1 week
                </Button>
              </div>
              <Calendar
                mode="single"
                selected={selected}
                defaultMonth={selected}
                onSelect={pick}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </Tooltip>
      </TooltipProvider>

      <ConfirmationDialog
        isOpen={!!pendingDate}
        onClose={() => {
          if (!mutation.isPending) setPendingDate(null);
        }}
        onConfirm={() => {
          if (pendingDate) mutation.mutate(format(pendingDate, "yyyy-MM-dd"));
        }}
        title="Change due date?"
        description={
          pendingDate ? `Set the due date to ${format(pendingDate, "MMM d, yyyy")}?` : ""
        }
        confirmText="Change due date"
        isLoading={mutation.isPending}
      />
    </>
  );
};

/** Inline quick-note — popover with a single textarea. */
const AddNotePopoverCell: React.FC<{ task: Task }> = ({ task }) => {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [content, setContent] = useState("");

  const mutation = useMutation({
    mutationFn: (text: string) =>
      createNote({ entity_type: "task", entity_id: task.id, content: text }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notes", "task", task.id] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      toast.success("Note added");
      setContent("");
      setConfirmOpen(false);
      setOpen(false);
    },
    onError: (error: any) => {
      toast.error("Could not add note", {
        description: error?.response?.data?.detail || error?.message,
      });
    },
  });

  // Clicking "Save note" stages the note for confirmation (no API call yet).
  const submit = () => {
    if (content.trim()) {
      setOpen(false);
      setConfirmOpen(true);
    }
  };

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <Popover open={open} onOpenChange={setOpen}>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <Button variant="ghost" className={iconBtn} title="Add note">
                  <StickyNote className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent>Add note</TooltipContent>
            <PopoverContent
              align="end"
              className="w-72 p-3 space-y-2"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-xs font-medium text-muted-foreground">Quick note</div>
              <Textarea
                autoFocus
                rows={3}
                placeholder="Type a note…"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                onKeyDown={(e) => {
                  if ((e.metaKey || e.ctrlKey) && e.key === "Enter") submit();
                }}
              />
              <Button
                size="sm"
                className="w-full h-8"
                disabled={!content.trim() || mutation.isPending}
                onClick={submit}
              >
                Save note
              </Button>
            </PopoverContent>
          </Popover>
        </Tooltip>
      </TooltipProvider>

      <ConfirmationDialog
        isOpen={confirmOpen}
        onClose={() => {
          if (!mutation.isPending) setConfirmOpen(false);
        }}
        onConfirm={() => {
          const text = content.trim();
          if (text) mutation.mutate(text);
        }}
        title="Add note?"
        description={`Add this note to the task?\n\n"${content.trim()}"`}
        confirmText="Add note"
        isLoading={mutation.isPending}
      />
    </>
  );
};

/** Inline mark-complete — single icon with a confirmation step. */
const CompleteButtonCell: React.FC<{ task: Task }> = ({ task }) => {
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const mutation = useMutation({
    mutationFn: () => taskService.markComplete(task.id),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      toast.success("Task completed", {
        description: `"${updated.title}" has been marked as completed.`,
      });
      setConfirmOpen(false);
    },
    onError: (error: any) => {
      toast.error("Could not complete task", {
        description: error?.response?.data?.detail || error?.message,
      });
    },
  });

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              className={iconBtn}
              title="Mark complete"
              onClick={() => setConfirmOpen(true)}
            >
              <CheckCircle2 className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Mark complete</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <ConfirmationDialog
        isOpen={confirmOpen}
        onClose={() => {
          if (!mutation.isPending) setConfirmOpen(false);
        }}
        onConfirm={() => mutation.mutate()}
        title="Mark task complete?"
        description={`Mark "${task.title}" as completed?`}
        confirmText="Mark complete"
        isLoading={mutation.isPending}
      />
    </>
  );
};

/** Compact inline action group for a task row. */
export const TaskRowActions: React.FC<{ task: Task }> = ({ task }) => {
  const isOpen = task.status === TaskStatus.OPEN;
  return (
    <div className="flex items-center justify-end gap-0.5">
      {isOpen && <CompleteButtonCell task={task} />}

      <DueDatePopoverCell task={task} />
      <AddNotePopoverCell task={task} />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className={iconBtn}>
            <span className="sr-only">Open menu</span>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            className="cursor-pointer"
            onClick={() => dispatchTaskEvent("view-task", task)}
          >
            <Eye className="mr-2 h-4 w-4" />
            <span>View Details</span>
          </DropdownMenuItem>

          {task.team_id && isOpen && !task.assigned_to_user_id && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={() => dispatchTaskEvent("claim-task", task)}
              >
                <UserPlus className="mr-2 h-4 w-4" />
                <span>Claim Task</span>
              </DropdownMenuItem>
            </>
          )}
          {task.team_id && isOpen && task.assigned_to_user_id && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={() => dispatchTaskEvent("release-task", task)}
              >
                <Undo2 className="mr-2 h-4 w-4" />
                <span>Release to Pool</span>
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default TaskRowActions;
